import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { getSmerList, ResponseGetSmerList } from './api/getSmerList';
import { useParams } from 'react-router-dom';
import { ItemSmer } from './components/ItemSmer';

import { useInView } from 'react-intersection-observer';
import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import i18n from '@/localisation/i18n';
import { FadeReplace } from '@/UI/animations/FadeReplace';
import { HeaderInfo } from '@/UI/HeaderInfo';
import {
  smerMarkAllViewed,
  SubmitSmerMarkAllViewed,
} from './api/smerMarkAllViewed';
import { useUnread } from '@/hooks/useUnread';
import { aggregateDataByDate } from '../Body/helper';
import { useClient } from '@/hooks/useClient';

export const Smer = () => {
  const currentClient = useClient();
  const { client } = useParams();
  const { isSmerUnread } = useUnread();
  const queryClient = useQueryClient();

  const storageKey = `smer_scroll_${client}`;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
  } = useInfiniteQuery<ResponseGetSmerList>({
    queryKey: ['smerList', client],

    queryFn: ({ pageParam }) =>
      getSmerList({ user: client!, offset: pageParam as number }),
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) {
        return undefined;
      }

      return lastPage.length < 20 ? undefined : allPages.length * 20;
    },
  });

  useEffect(() => {
    const savedPosition = sessionStorage.getItem(storageKey);
    const scrollElement = document.querySelector('main'); // Получаем элемент
    if (savedPosition && scrollElement) {
      scrollElement.scrollTop = Number(savedPosition);
    }
  }, [storageKey]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollElement = document.querySelector('main');
      if (scrollElement) {
        sessionStorage.setItem(storageKey, scrollElement.scrollTop.toString());
      }
    };

    const scrollElement = document.querySelector('main');
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [storageKey]);

  // Хук для отслеживания видимости элемента
  const { ref, inView } = useInView({
    threshold: 1.0,
    triggerOnce: false,
  });

  const { mutate } = useMutation({
    mutationFn: (data: SubmitSmerMarkAllViewed) => smerMarkAllViewed(data),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const aggregation = useMemo(() => {
    if (!data?.pages.length || !data.pages[0]) return [];

    const list = data?.pages.reduce((acc, page) => acc.concat(page), []) || [];

    return aggregateDataByDate(list);
  }, [client, data?.pages.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <HeaderInfo
        title={i18n.t('smer')}
        subtitle={currentClient?.userName ?? ''}
        backUrl={`/${client}`}
        actionButton={
          <button
            disabled={!isSmerUnread || aggregation.length === 0}
            onClick={() => mutate({ user: client! })}
            className="btn btn-primary w-full text-sm font-bold"
          >
            <svg
              width="16"
              height="16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.6 13.456A5.97 5.97 0 0 1 12 12.4c.568 0 1.117.08 1.636.226a.6.6 0 0 0 .764-.577v-8.8a.6.6 0 0 0-.437-.578A7.205 7.205 0 0 0 12 2.401a7.17 7.17 0 0 0-3.4.851v10.204ZM7.4 3.252A7.17 7.17 0 0 0 4 2.4c-.68 0-1.339.095-1.964.271a.6.6 0 0 0-.436.578v8.8a.6.6 0 0 0 .763.577A6.003 6.003 0 0 1 4 12.4a5.97 5.97 0 0 1 3.4 1.056V3.252Z"
                fill="currentColor"
              />
            </svg>
            {i18n.t('mark.all.read')}
          </button>
        }
      >
        <div className="mt-8">
          {aggregation?.length === 0 && !isFetching ? (
            <div className="flex h-32 items-center justify-center text-center text-additional">
              {i18n.t('no.records')}
            </div>
          ) : (
            <FadeReplace
              firstElement={
                <div className="mt-3 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  <div className="skeleton h-36 w-full"></div>
                  <div className="skeleton h-36 w-full"></div>
                  <div className="skeleton h-36 w-full"></div>
                  <div className="skeleton h-36 w-full"></div>
                  <div className="skeleton h-36 w-full"></div>
                  <div className="skeleton h-36 w-full"></div>
                  <div className="skeleton h-36 w-full"></div>
                  <div className="skeleton h-36 w-full"></div>
                  <div className="skeleton h-36 w-full"></div>
                </div>
              }
              secondElement={
                <div>
                  {aggregation.map(item => (
                    <div key={item.date} className="mt-8">
                      <h4 className="text-base font-semibold">{item.date}</h4>
                      <div className="mt-3 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                        {item.list.map(smer => (
                          <ItemSmer key={smer.id} client={client!} {...smer} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              }
              showFirst={
                aggregation.length === 0 || (isFetching && !isFetchingNextPage)
              }
            />
          )}
        </div>
        <div ref={ref} className="loading-indicator">
          {isFetchingNextPage && data && (
            <div className="mt-4 flex justify-center">
              <span className="loading loading-ring loading-lg"></span>
            </div>
          )}
        </div>
      </HeaderInfo>
    </motion.div>
  );
};
